import { useState, useEffect, useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import Layout from '@/components/Layout';
import { Documento } from '@/types';
import { useAuth } from '@/lib/authContext';
import { supabase } from '@/lib/supabase';
import FilterBar, { Filter } from '@/components/FilterBar';
import DocumentViewer from '@/components/DocumentViewer';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/* ═══════════════════════════════════════════
   Status configuration
   ═══════════════════════════════════════════ */

const STATUS_CONFIG: Record<string, { label: string; dotColor: string; badgeBg: string; badgeText: string }> = {
  pendiente: { label: 'Pendiente', dotColor: 'bg-amber-400', badgeBg: 'bg-amber-50/50', badgeText: 'text-amber-600' },
  procesado_ia: { label: 'Procesado IA', dotColor: 'bg-blue-400', badgeBg: 'bg-blue-50/50', badgeText: 'text-blue-600' },
  validado: { label: 'Validado', dotColor: 'bg-emerald-400', badgeBg: 'bg-emerald-50/50', badgeText: 'text-emerald-600' },
  contabilizado: { label: 'Contabilizado', dotColor: 'bg-teal-400', badgeBg: 'bg-teal-50/50', badgeText: 'text-teal-600' },
  incidencia: { label: 'Incidencia', dotColor: 'bg-red-400', badgeBg: 'bg-red-50/50', badgeText: 'text-red-500' },
  rechazado: { label: 'Rechazado', dotColor: 'bg-slate-300', badgeBg: 'bg-slate-50', badgeText: 'text-calm-muted' },
};

const STATUS_PILLS: Array<{ key: string | null; label: string; matchEstados: string[] }> = [
  { key: 'pendiente', label: 'Pendiente', matchEstados: ['pendiente'] },
  { key: 'procesado_ia', label: 'Procesado IA', matchEstados: ['procesado_ia'] },
  { key: 'validado', label: 'Validado', matchEstados: ['validado'] },
  { key: 'contabilizado', label: 'Contabilizado', matchEstados: ['contabilizado'] },
  { key: 'incidencia', label: 'Incidencia', matchEstados: ['incidencia'] },
  { key: 'rechazado', label: 'Rechazado', matchEstados: ['rechazado'] },
  { key: null, label: 'Todos', matchEstados: [] },
];

type SortField = 'nombre' | 'cliente' | 'tipo' | 'estado' | 'fecha' | 'confianza';
type SortDir = 'asc' | 'desc';

/* ═══════════════════════════════════════════
   Component
   ═══════════════════════════════════════════ */

export default function AsesorDocumentos() {
  const { user, loading } = useAuth();
  const router = useRouter();
  const [documentos, setDocumentos] = useState<any[]>([]);
  const [clientes, setClientes] = useState<any[]>([]);
  const [filtros, setFiltros] = useState<Record<string, any>>({});
  const [documentoSeleccionado, setDocumentoSeleccionado] = useState<any | null>(null);
  const [mostrarVisor, setMostrarVisor] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  // New state for upgraded features
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('fecha');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  /* ──────────── Data loading ──────────── */

  const loadDocuments = useCallback(async () => {
    try {
      setLoadingData(true);

      // Get advisor's assigned clients
      const { data: assignedClients, error: clientError } = await supabase
        .from('asesor_cliente')
        .select('cliente_id, clientes(*)')
        .eq('asesor_id', user?.id);

      if (clientError) throw clientError;

      const clientIds = assignedClients?.map(ac => ac.cliente_id) || [];
      setClientes(assignedClients?.map(ac => ac.clientes) || []);

      // Get all documents from assigned clients
      const { data: docs, error: docsError } = await supabase
        .from('documentos')
        .select('*, clientes(razon_social, nombre_comercial)')
        .in('cliente_id', clientIds)
        .order('fecha_subida', { ascending: false });

      if (docsError) throw docsError;

      setDocumentos(docs || []);
    } catch (error) {
      console.error('Error loading documents:', error);
      toast.error('Error al cargar documentos');
    } finally {
      setLoadingData(false);
    }
  }, [user]);

  useEffect(() => {
    if (!loading && (!user || user.rol !== 'asesor')) {
      router.push('/');
    }
    if (user && user.rol === 'asesor') {
      loadDocuments();
    }
  }, [user, loading, router, loadDocuments]);

  /* ──────────── Filters ──────────── */

  const filterConfig: Filter[] = [
    {
      key: 'buscar',
      label: 'Buscar',
      type: 'text',
      placeholder: 'Nombre documento, cliente...',
    },
    {
      key: 'cliente',
      label: 'Cliente',
      type: 'select',
      options: clientes.map((c: any) => ({ value: c.id, label: c.razon_social })),
    },
    {
      key: 'tipo',
      label: 'Tipo',
      type: 'select',
      options: [
        { value: 'emitida_pdf', label: 'Factura Emitida (PDF)' },
        { value: 'recibida_pdf', label: 'Factura Recibida (PDF)' },
        { value: 'emitida_excel', label: 'Factura Emitida (Excel)' },
        { value: 'recibida_excel', label: 'Factura Recibida (Excel)' },
        { value: 'otro', label: 'Otro' },
      ],
    },
    {
      key: 'estado',
      label: 'Estado',
      type: 'select',
      options: [
        { value: 'pendiente', label: 'Pendiente' },
        { value: 'procesado_ia', label: 'Procesado IA' },
        { value: 'validado', label: 'Validado' },
        { value: 'contabilizado', label: 'Contabilizado' },
        { value: 'incidencia', label: 'Con Incidencia' },
      ],
    },
  ];

  const documentosFiltrados = useMemo(() => {
    let filtered = documentos.filter(doc => {
      if (filtros.buscar) {
        const busqueda = filtros.buscar.toLowerCase();
        const clienteNombre = doc.clientes?.razon_social || doc.clientes?.nombre_comercial || '';
        if (!doc.nombre.toLowerCase().includes(busqueda) &&
            !clienteNombre.toLowerCase().includes(busqueda)) {
          return false;
        }
      }
      if (filtros.cliente && doc.cliente_id !== filtros.cliente) return false;
      if (filtros.tipo && doc.tipo !== filtros.tipo) return false;
      if (filtros.estado && doc.estado !== filtros.estado) return false;
      return true;
    });

    // Apply status pill filter
    if (statusFilter !== null) {
      const activePill = STATUS_PILLS.find(p => p.key === statusFilter);
      if (activePill) {
        filtered = filtered.filter(d => activePill.matchEstados.includes(d.estado || ''));
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'nombre':
          cmp = (a.nombre || '').localeCompare(b.nombre || '');
          break;
        case 'cliente': {
          const cA = a.clientes?.razon_social || '';
          const cB = b.clientes?.razon_social || '';
          cmp = cA.localeCompare(cB);
          break;
        }
        case 'tipo':
          cmp = (a.tipo || '').localeCompare(b.tipo || '');
          break;
        case 'estado':
          cmp = (a.estado || '').localeCompare(b.estado || '');
          break;
        case 'fecha':
          cmp = new Date(a.fecha_subida).getTime() - new Date(b.fecha_subida).getTime();
          break;
        case 'confianza': {
          const confA = a.datos_ia?.nivelConfianza ?? -1;
          const confB = b.datos_ia?.nivelConfianza ?? -1;
          cmp = confA - confB;
          break;
        }
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    return filtered;
  }, [documentos, filtros, statusFilter, sortField, sortDir]);

  /* ──────────── Status counts ──────────── */

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const pill of STATUS_PILLS) {
      if (pill.key === null) {
        counts['todos'] = documentos.length;
      } else {
        counts[pill.key] = documentos.filter(d => pill.matchEstados.includes(d.estado || '')).length;
      }
    }
    return counts;
  }, [documentos]);

  const estadisticas = useMemo(() => ({
    total: documentos.length,
    pendientes: documentos.filter(d => d.estado === 'pendiente').length,
    procesadosIA: documentos.filter(d => d.estado === 'procesado_ia').length,
    validados: documentos.filter(d => d.estado === 'validado').length,
    contabilizados: documentos.filter(d => d.estado === 'contabilizado').length,
    incidencias: documentos.filter(d => d.estado === 'incidencia').length,
  }), [documentos]);

  /* ──────────── Handlers ──────────── */

  const handleFiltroChange = (key: string, value: any) => {
    setFiltros({ ...filtros, [key]: value });
  };

  const handleLimpiarFiltros = () => {
    setFiltros({});
    setStatusFilter(null);
  };

  const handleVerDocumento = (doc: Documento) => {
    setDocumentoSeleccionado(doc);
    setMostrarVisor(true);
  };

  const handleValidarDocumento = async (doc: any) => {
    try {
      const { error } = await supabase
        .from('documentos')
        .update({ estado: 'validado' })
        .eq('id', doc.id);

      if (error) throw error;

      toast.success(`Documento ${doc.nombre} validado correctamente`);
      loadDocuments();
    } catch (error) {
      console.error('Error validating document:', error);
      toast.error('Error al validar documento');
    }
  };

  /* ──────────── Sorting ──────────── */

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => (
    <span className="inline-flex flex-col ml-1 -space-y-1">
      <svg className={cn('w-3 h-3', sortField === field && sortDir === 'asc' ? 'text-calm-text' : 'text-calm-accent')} viewBox="0 0 10 6" fill="currentColor">
        <path d="M5 0L10 6H0z" />
      </svg>
      <svg className={cn('w-3 h-3', sortField === field && sortDir === 'desc' ? 'text-calm-text' : 'text-calm-accent')} viewBox="0 0 10 6" fill="currentColor">
        <path d="M5 6L0 0h10z" />
      </svg>
    </span>
  );

  /* ──────────── Selection ──────────── */

  const toggleDocSelection = (docId: string) => {
    setSelectedDocs(prev => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  };

  const allTableSelected = documentosFiltrados.length > 0 && documentosFiltrados.every(d => selectedDocs.has(d.id));

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const next = new Set(selectedDocs);
      documentosFiltrados.forEach(d => next.add(d.id));
      setSelectedDocs(next);
    } else {
      const next = new Set(selectedDocs);
      documentosFiltrados.forEach(d => next.delete(d.id));
      setSelectedDocs(next);
    }
  };

  /* ──────────── Bulk actions ──────────── */

  const handleBulkDownload = () => {
    if (selectedDocs.size === 0) {
      toast.error('No hay documentos seleccionados');
      return;
    }
    const docsToDownload = documentosFiltrados.filter(d => selectedDocs.has(d.id) && d.url_archivo);
    if (docsToDownload.length === 0) {
      toast.error('Los documentos seleccionados no tienen archivos disponibles');
      return;
    }
    toast.success(`Descargando ${docsToDownload.length} documento(s)...`);
    docsToDownload.forEach((doc, index) => {
      setTimeout(() => {
        window.open(doc.url_archivo, '_blank');
      }, index * 500);
    });
  };

  const handleBulkChangeStatus = async (nuevoEstado: string) => {
    if (selectedDocs.size === 0) return;

    try {
      const { error } = await supabase
        .from('documentos')
        .update({ estado: nuevoEstado })
        .in('id', Array.from(selectedDocs));

      if (error) throw error;

      toast.success(`${selectedDocs.size} documento(s) actualizado(s) a "${STATUS_CONFIG[nuevoEstado]?.label || nuevoEstado}"`);
      setSelectedDocs(new Set());
      loadDocuments();
    } catch (error) {
      console.error('Error updating documents:', error);
      toast.error('Error al actualizar documentos');
    }
  };

  /* ──────────── CSV Export ──────────── */

  const handleExportCSV = () => {
    if (documentosFiltrados.length === 0) {
      toast.error('No hay documentos para exportar');
      return;
    }

    const headers = ['Documento', 'Cliente', 'Tipo', 'Estado', 'Fecha Subida', 'Confianza IA'];
    const rows = documentosFiltrados.map(doc => {
      const clienteNombre = doc.clientes?.nombre_comercial || doc.clientes?.razon_social || '';
      const tipoLabel =
        doc.tipo === 'emitida_pdf' ? 'Emitida (PDF)' :
        doc.tipo === 'recibida_pdf' ? 'Recibida (PDF)' :
        doc.tipo === 'emitida_excel' ? 'Emitida (Excel)' :
        doc.tipo === 'recibida_excel' ? 'Recibida (Excel)' : 'Otro';
      const estadoLabel = STATUS_CONFIG[doc.estado]?.label || doc.estado;
      const fecha = new Date(doc.fecha_subida).toLocaleDateString('es-ES');
      const confianza = doc.datos_ia?.nivelConfianza ? `${doc.datos_ia.nivelConfianza.toFixed(0)}%` : '';

      return [
        `"${(doc.nombre || '').replace(/"/g, '""')}"`,
        `"${clienteNombre.replace(/"/g, '""')}"`,
        `"${tipoLabel}"`,
        `"${estadoLabel}"`,
        `"${fecha}"`,
        `"${confianza}"`,
      ].join(';');
    });

    const csvContent = '\uFEFF' + [headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `documentos-todos-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Archivo CSV exportado correctamente');
  };

  /* ──────────── Helpers ──────────── */

  const getTipoLabel = (tipo: string) => {
    switch (tipo) {
      case 'emitida_pdf': return 'Emitida (PDF)';
      case 'recibida_pdf': return 'Recibida (PDF)';
      case 'emitida_excel': return 'Emitida (Excel)';
      case 'recibida_excel': return 'Recibida (Excel)';
      default: return 'Otro';
    }
  };

  const getStatusDot = (estado: string) => {
    const cfg = STATUS_CONFIG[estado];
    return cfg ? cfg.dotColor : 'bg-slate-300';
  };

  const getStatusBadge = (estado: string) => {
    const cfg = STATUS_CONFIG[estado];
    if (!cfg) return 'bg-slate-50 text-calm-muted';
    return `${cfg.badgeBg} ${cfg.badgeText}`;
  };

  const getStatusLabel = (estado: string) => {
    return STATUS_CONFIG[estado]?.label || estado;
  };

  const hasActiveFilters = Object.values(filtros).some(v => v) || statusFilter !== null;

  /* ──────────── Loading ──────────── */

  if (loading || loadingData) {
    return (
      <Layout rol="asesor">
        <div className="px-12 pt-10 pb-12 space-y-12">
          <div className="space-y-3">
            <Skeleton className="h-12 w-72" />
            <Skeleton className="h-4 w-44" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-huge" />
            ))}
          </div>
          <div className="flex gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-28 rounded-full" />
            ))}
          </div>
          <Skeleton className="h-64 rounded-huge" />
        </div>
      </Layout>
    );
  }

  /* ═══════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════ */

  return (
    <Layout rol="asesor">
      <div className="h-[calc(100vh-72px)] overflow-y-auto px-12 pt-10 pb-12 space-y-12">

        {/* ── Page Header ── */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-5xl font-display font-light text-calm-text">Todos los Documentos</h1>
            <p className="text-calm-muted mt-2">
              {documentosFiltrados.length} de {estadisticas.total} documento{estadisticas.total !== 1 ? 's' : ''}
              {hasActiveFilters && ' (filtrado)'}
            </p>
          </div>
          <button
            onClick={handleExportCSV}
            className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:text-calm-text transition-colors inline-flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-[18px]">download</span>
            Exportar Excel
          </button>
        </div>

        {/* ── Stats Cards ── */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { label: 'Total', value: estadisticas.total, color: 'text-calm-text' },
            { label: 'Pendientes', value: estadisticas.pendientes, color: 'text-amber-500' },
            { label: 'Procesados IA', value: estadisticas.procesadosIA, color: 'text-blue-500' },
            { label: 'Validados', value: estadisticas.validados, color: 'text-emerald-500' },
            { label: 'Contabilizados', value: estadisticas.contabilizados, color: 'text-teal-500' },
            { label: 'Incidencias', value: estadisticas.incidencias, color: 'text-red-400' },
          ].map(stat => (
            <div key={stat.label} className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge p-6 hover:shadow-sm transition-shadow">
              <div className="text-[10px] uppercase tracking-widest text-calm-muted">{stat.label}</div>
              <div className={cn('text-2xl font-display font-light mt-2', stat.color)}>{stat.value}</div>
            </div>
          ))}
        </div>

        {/* ── Status Filter Pills ── */}
        <div className="flex flex-wrap items-center gap-3">
          {STATUS_PILLS.map(pill => {
            const isActive = statusFilter === pill.key;
            const count = pill.key === null
              ? (statusCounts['todos'] || 0)
              : (statusCounts[pill.key] || 0);
            return (
              <button
                key={pill.label}
                className={cn(
                  'px-6 py-2 rounded-full text-sm font-light border transition-colors inline-flex items-center gap-2',
                  isActive
                    ? 'border-calm-text text-calm-text bg-white'
                    : 'bg-white border-calm-border text-calm-muted hover:border-calm-text hover:text-calm-text'
                )}
                onClick={() => setStatusFilter(isActive && pill.key !== null ? null : pill.key)}
              >
                {pill.label}
                <span className={cn('text-[10px] tabular-nums', isActive ? 'opacity-60' : 'text-calm-accent')}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* ── FilterBar in Card ── */}
        <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge overflow-hidden">
          <div className="p-1">
            <FilterBar
              filters={filterConfig}
              values={filtros}
              onChange={handleFiltroChange}
              onClear={handleLimpiarFiltros}
            />
            {hasActiveFilters && (
              <div className="px-4 pb-3 flex justify-end">
                <button
                  onClick={handleLimpiarFiltros}
                  className="text-sm font-light text-calm-muted hover:text-calm-text transition-colors"
                >
                  Limpiar todos los filtros
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Bulk Actions Bar ── */}
        {selectedDocs.size > 0 && (
          <div className="bg-gradient-to-br from-white to-emerald-50/20 border border-emerald-200/40 rounded-huge p-6">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <span className="text-sm font-light text-calm-text">
                {selectedDocs.size} documento{selectedDocs.size !== 1 ? 's' : ''} seleccionado{selectedDocs.size !== 1 ? 's' : ''}
              </span>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleBulkDownload}
                  className="border border-calm-border rounded-huge px-5 py-2 text-sm font-light text-calm-muted hover:text-calm-text transition-colors inline-flex items-center gap-2"
                >
                  <span className="material-symbols-outlined text-[16px]">download</span>
                  Descargar ZIP
                </button>
                <div className="relative group">
                  <button className="border border-calm-border rounded-huge px-5 py-2 text-sm font-light text-calm-muted hover:text-calm-text transition-colors inline-flex items-center gap-1">
                    Cambiar estado
                    <span className="material-symbols-outlined text-[16px]">expand_more</span>
                  </button>
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-huge shadow-lg border border-calm-border py-1 z-20 hidden group-hover:block">
                    {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                      <button
                        key={key}
                        onClick={() => handleBulkChangeStatus(key)}
                        className="w-full text-left px-4 py-2 text-sm font-light text-calm-muted hover:bg-slate-50 hover:text-calm-text flex items-center gap-2 transition-colors"
                      >
                        <span className={cn('w-2 h-2 rounded-full', cfg.dotColor)} />
                        {cfg.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedDocs(new Set())}
                  className="text-sm font-light text-calm-accent hover:text-calm-text transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Documents Table ── */}
        <div className="bg-gradient-to-br from-white to-fresh-sky-50/20 border border-fresh-sky-200/40 rounded-huge overflow-hidden">
          <div className="px-8 py-5 border-b border-fresh-sky-200/40">
            <h2 className="text-sm font-light text-calm-text">
              Detalle de Documentos
              <span className="ml-2 text-calm-muted">
                ({documentosFiltrados.length} resultado{documentosFiltrados.length !== 1 ? 's' : ''})
              </span>
            </h2>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10 pl-6">
                  <Checkbox
                    checked={allTableSelected}
                    onCheckedChange={(checked) => handleSelectAll(!!checked)}
                  />
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none whitespace-nowrap text-[11px] uppercase tracking-widest text-calm-muted hover:text-calm-text"
                  onClick={() => handleSort('nombre')}
                >
                  <span className="inline-flex items-center">
                    Documento
                    <SortIcon field="nombre" />
                  </span>
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none whitespace-nowrap text-[11px] uppercase tracking-widest text-calm-muted hover:text-calm-text"
                  onClick={() => handleSort('cliente')}
                >
                  <span className="inline-flex items-center">
                    Cliente
                    <SortIcon field="cliente" />
                  </span>
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none whitespace-nowrap text-[11px] uppercase tracking-widest text-calm-muted hover:text-calm-text"
                  onClick={() => handleSort('tipo')}
                >
                  <span className="inline-flex items-center">
                    Tipo
                    <SortIcon field="tipo" />
                  </span>
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none whitespace-nowrap text-[11px] uppercase tracking-widest text-calm-muted hover:text-calm-text"
                  onClick={() => handleSort('estado')}
                >
                  <span className="inline-flex items-center">
                    Estado
                    <SortIcon field="estado" />
                  </span>
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none whitespace-nowrap text-[11px] uppercase tracking-widest text-calm-muted hover:text-calm-text"
                  onClick={() => handleSort('confianza')}
                >
                  <span className="inline-flex items-center">
                    Confianza IA
                    <SortIcon field="confianza" />
                  </span>
                </TableHead>
                <TableHead
                  className="cursor-pointer select-none whitespace-nowrap text-[11px] uppercase tracking-widest text-calm-muted hover:text-calm-text"
                  onClick={() => handleSort('fecha')}
                >
                  <span className="inline-flex items-center">
                    Fecha
                    <SortIcon field="fecha" />
                  </span>
                </TableHead>
                <TableHead className="text-center whitespace-nowrap text-[11px] uppercase tracking-widest text-calm-muted pr-6">
                  Acciones
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documentosFiltrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="px-6 py-16 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-fresh-sky-50 flex items-center justify-center text-fresh-sky-400 mb-4 mx-auto">
                      <span className="material-symbols-outlined text-[48px]">folder_open</span>
                    </div>
                    <p className="text-calm-muted font-light">No hay documentos que coincidan con los filtros</p>
                    {hasActiveFilters && (
                      <button
                        onClick={handleLimpiarFiltros}
                        className="mt-3 text-sm font-light text-calm-muted hover:text-calm-text transition-colors"
                      >
                        Limpiar filtros
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                documentosFiltrados.map(doc => {
                  const clienteNombre = doc.clientes?.nombre_comercial || doc.clientes?.razon_social || 'Cliente';
                  const isSelected = selectedDocs.has(doc.id);
                  return (
                    <TableRow
                      key={doc.id}
                      className={cn('group cursor-pointer hover:bg-slate-50/50', isSelected && 'bg-slate-50/30')}
                      onClick={() => handleVerDocumento(doc)}
                    >
                      <TableCell className="pl-6" onClick={e => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleDocSelection(doc.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-light text-calm-text max-w-[200px] truncate" title={doc.nombre}>
                          {doc.nombre}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-light text-calm-muted max-w-[180px] truncate" title={clienteNombre}>
                          {clienteNombre}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <span className="text-sm font-light text-calm-muted">{getTipoLabel(doc.tipo)}</span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <span className={cn('inline-flex items-center gap-1.5 text-sm font-light', getStatusBadge(doc.estado))}>
                          <span className={cn('w-2 h-2 rounded-full', getStatusDot(doc.estado))} />
                          {getStatusLabel(doc.estado)}
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {doc.datos_ia?.nivelConfianza ? (
                          <span className={cn('text-sm font-light',
                            doc.datos_ia?.nivelConfianza >= 95 ? 'text-emerald-500' :
                            doc.datos_ia?.nivelConfianza >= 80 ? 'text-amber-500' :
                            'text-red-400'
                          )}>
                            {doc.datos_ia?.nivelConfianza.toFixed(0)}%
                          </span>
                        ) : (
                          <span className="text-sm text-calm-accent">—</span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <span className="text-sm font-light text-calm-muted">
                          {new Date(doc.fecha_subida).toLocaleDateString('es-ES')}
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap pr-6" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            className="p-1.5 rounded-lg text-calm-accent hover:text-calm-text hover:bg-slate-50 transition-colors"
                            onClick={() => handleVerDocumento(doc)}
                            title="Ver documento"
                          >
                            <span className="material-symbols-outlined text-[18px]">visibility</span>
                          </button>
                          {(doc.estado === 'procesado_ia' || doc.estado === 'pendiente') && (
                            <button
                              className="p-1.5 rounded-lg text-calm-accent hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                              onClick={() => handleValidarDocumento(doc)}
                              title="Validar documento"
                            >
                              <span className="material-symbols-outlined text-[18px]">check_circle</span>
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* ── Document Viewer Modal ── */}
        <Dialog open={mostrarVisor && !!documentoSeleccionado} onOpenChange={(open) => {
          if (!open) {
            setMostrarVisor(false);
            setDocumentoSeleccionado(null);
          }
        }}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-display font-light text-calm-text">{documentoSeleccionado?.nombre}</DialogTitle>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-sm font-light text-calm-muted">
                  {documentoSeleccionado?.clientes?.nombre_comercial || documentoSeleccionado?.clientes?.razon_social}
                </span>
                {documentoSeleccionado && (
                  <span className={cn('inline-flex items-center gap-1.5 text-sm font-light', getStatusBadge(documentoSeleccionado.estado))}>
                    <span className={cn('w-2 h-2 rounded-full', getStatusDot(documentoSeleccionado.estado))} />
                    {getStatusLabel(documentoSeleccionado.estado)}
                  </span>
                )}
              </div>
            </DialogHeader>

            {documentoSeleccionado && (
              <div>
                <DocumentViewer documento={documentoSeleccionado} />

                <div className="mt-6 flex flex-wrap gap-3">
                  {(documentoSeleccionado.estado === 'procesado_ia' || documentoSeleccionado.estado === 'pendiente') && (
                    <>
                      <button
                        onClick={() => handleValidarDocumento(documentoSeleccionado)}
                        className="bg-calm-text text-white rounded-huge px-5 py-2.5 text-sm font-light hover:bg-calm-text/90 transition-colors inline-flex items-center gap-2"
                      >
                        <span className="material-symbols-outlined text-[18px]">check_circle</span>
                        Validar Documento
                      </button>
                      <button className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:text-calm-text transition-colors inline-flex items-center gap-2">
                        <span className="material-symbols-outlined text-[18px]">warning</span>
                        Crear Incidencia
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => {
                      if (documentoSeleccionado?.url_archivo) {
                        window.open(documentoSeleccionado.url_archivo, '_blank');
                        toast.success('Abriendo documento...');
                      } else {
                        toast.error('No se encontró el archivo del documento');
                      }
                    }}
                    className="border border-calm-border rounded-huge px-5 py-2.5 text-sm font-light text-calm-muted hover:bg-white hover:text-calm-text transition-colors inline-flex items-center gap-2"
                  >
                    <span className="material-symbols-outlined text-[18px]">download</span>
                    Descargar
                  </button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
